import { EndUserCertKeyType } from '@/sign/models/sign-constants';
import { SignPrivateKeyStrategy } from '@/sign/strategies/sign-private-key.strategy';
import { DiiaSignExecutor } from '@/sign/models/diia-sign.model';
import { determineHashAlgorithm, isSealCertificates } from '../helpers/sign.helper';
import { getLocale } from '@/helpers/locale-helper';
import { FileHashingResult, FileSigningResult } from '@/models/document-sign.models';
import { base64ToArrayBuffer } from '@/helpers';
import { utilityService } from '@/services/utility.service';
import { getRecaptchaTokenIfNotAuthenticated } from '@/helpers/recaptha-helper';

type Constructor<T> = new (...args: any[]) => T;

export function MixWithDiiaKeyType<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends Base {
    protected async determineKeyTypes(): Promise<void> {
      this.keyType = EndUserCertKeyType.DSTU4145;
    }
  };
}

export function MixWithDiiaSigning<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends MixWithDiiaKeyType(Base) {
    protected async signFileHashesWithDiia(hashes: Array<any>): Promise<Array<any>> {
      if (!hashes || !hashes.length) {
        throw new Error('File hash list is required');
      }

      let signedHashes;
      const diiaSignExecutor = new DiiaSignExecutor(hashes, this.signPurpose, this.isSeal);
      try {
        signedHashes = await diiaSignExecutor.sign();
      } finally {
        diiaSignExecutor.destroy();
      }

      if (!signedHashes || !signedHashes.length) {
        throw new Error('Empty sign result');
      }

      return signedHashes;
    }

    protected makeFileNamesUnique(hashes: Array<any>) {
      const fileNamesCounters = {};

      hashes.forEach(h => {
        let fn = h.fileName;
        if (fn in fileNamesCounters) {
          let nameParts = fn.split('.');
          let partToChangeIndex = nameParts.length > 1 ? nameParts.length - 2 : nameParts.length - 1;
          nameParts[partToChangeIndex] = `${nameParts[partToChangeIndex]} (${fileNamesCounters[fn]})`;
          fn = nameParts.join('.');
          fileNamesCounters[fn] += 1;
        } else {
          fileNamesCounters[fn] = 1;
        }
        h.fileName = fn;
      });
    }
  };
}

export function MixWithDiiaDocumentSigning<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends MixWithDiiaSigning(Base) {
    protected async isKeyPrivateKeyReaded(): Promise<boolean> {
      return true;
    }

    protected async signKeyTypeHashes(keyType: number, hashes: Array<FileHashingResult>, lib: any): Promise<Array<FileSigningResult>> {
      const hashAlgo = determineHashAlgorithm(this.keyType);
      for (const hash of hashes) {
        if (hash.isInternalNested) {
          let signContent = base64ToArrayBuffer(hash.sign);
          hash.hasheBase64 = await this.euSignLib.HashData(hashAlgo, signContent, true);
        }
      }

      const mappedRelation: any[] = [];

      const mappedHashes = hashes.map(h => {
        let mappedHash = {
          pDocumentVersionsFileCode: h.pDocumentVersionsFileCode,
          fileName: h.fileName,
          fileHash: h.hasheBase64
        };
        mappedRelation.push({
          hash: h,
          mappedHash: mappedHash
        });
        return mappedHash;
      });

      this.makeFileNamesUnique(mappedHashes);

      let signedHashes = await this.signFileHashesWithDiia(mappedHashes);
      if (this.signingParams.validateKeyType) {
        let certs = signedHashes && signedHashes.length > 0
          ? await this.euSignLib.GetSigner(signedHashes[0].signature, -1, false)
          : [];
        if (!this.isSeal && isSealCertificates(certs)) {
          throw new Error(getLocale('appSign.errors.selectedKeyIsNotSigningKey').toString());
        } else if (this.isSeal && !isSealCertificates(certs)) {
          throw new Error(getLocale('appSign.errors.selectedKeyIsNotSeal').toString());
        }
      }

      let signResults: FileSigningResult[] = [];
      for (let signedHash of signedHashes) {
        let hash = mappedRelation.find(r => r.mappedHash.fileName === signedHash.name)!.hash;
        let signResult = new FileSigningResult({
          pDocumentVersionsFileCode: hash.pDocumentVersionsFileCode,
          pSignBase64: null,
          isInternalNested: hash.isInternalNested,
          documentVerFileSignDataId: hash.documentVerFileSignDataId
        });

        if (hash.isInternalNested) {
          let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

          let creationResult = await utilityService.createInternalNestedSign(hash.sign, signedHash.signature, recaptchaToken, recaptchaType);
          if (!creationResult || !creationResult.newSignature) {
            throw new Error('Failed to create signature');
          }
          signResult.pSignBase64 = creationResult.newSignature;
        } else {
          signResult.pSignBase64 = signedHash.signature;
        }

        signResults.push(signResult);
      }

      return signResults;
    }
  };
}
